import { ChangeEvent, ReactNode, forwardRef, useId } from "react";
import styles from "./NumberInputWithUnit.module.css";
import type { HelperVariant } from "../Input/Input";

export interface NumberInputWithUnitProps {
  label?: string;
  hint?: string;
  hintRight?: ReactNode;
  /** Unit suffix shown inside the input (e.g. "msgs", "chars", "/min"). */
  unit: string;
  value: number | string;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  modified?: boolean;
  error?: string;
  helperVariant?: HelperVariant;
  /** Optional helper text rendered under the input (e.g. "= 1 hour"). */
  helperText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  placeholder?: string;
  id?: string;
  name?: string;
  className?: string;
  "aria-label"?: string;
}

export const NumberInputWithUnit = forwardRef<
  HTMLInputElement,
  NumberInputWithUnitProps
>(function NumberInputWithUnit(
  {
    label,
    hint,
    hintRight,
    unit,
    value,
    onChange,
    min,
    max,
    step,
    modified,
    error,
    helperVariant = "default",
    helperText,
    disabled,
    readOnly,
    required,
    placeholder,
    id,
    name,
    className = "",
    "aria-label": ariaLabel,
  },
  ref,
) {
  const reactId = useId();
  const fid = id ?? reactId;

  const helper = error ?? helperText ?? hint;
  const helperVariantResolved: HelperVariant = error
    ? "error"
    : helperVariant;

  const helperClass =
    helperVariantResolved === "warn"
      ? styles.helperWarn
      : helperVariantResolved === "error"
        ? styles.helperError
        : helperVariantResolved === "ok"
          ? styles.helperOk
          : styles.helper;

  const inputClass = [
    styles.input,
    error ? styles.error : "",
    !error && modified ? styles.modified : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    // Forward the parsed numeric value; if blank, send NaN so callers can
    // decide their own clamping/default behavior.
    const raw = e.target.value;
    if (raw === "") {
      onChange(NaN);
      return;
    }
    const n = Number(raw);
    onChange(Number.isNaN(n) ? NaN : n);
  }

  return (
    <div className={styles.wrap}>
      {(label || hintRight) && (
        <div className={hintRight ? styles.labelRow : undefined}>
          {label && (
            <label className={styles.label} htmlFor={fid}>
              {label}
            </label>
          )}
          {hintRight && <span className={styles.hintRight}>{hintRight}</span>}
        </div>
      )}
      <div className={styles.unitWrap}>
        <input
          ref={ref}
          id={fid}
          name={name}
          type="number"
          className={inputClass}
          value={Number.isNaN(value as number) ? "" : value}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-invalid={!!error || undefined}
          aria-describedby={
            error ? `${fid}-err` : helper ? `${fid}-help` : undefined
          }
        />
        <span className={styles.unit} aria-hidden>
          {unit}
        </span>
      </div>
      {error && (
        <div id={`${fid}-err`} className={styles.helperError}>
          {error}
        </div>
      )}
      {!error && helper && (
        <div id={`${fid}-help`} className={helperClass}>
          {helper}
        </div>
      )}
    </div>
  );
});
